import { Button, Card, Form, Input, InputNumber, Select, message } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import type { FormInstance } from "antd";
import { useEffect, useState } from "react";
import type { ChangeEvent } from "react";
import { useNavigate, useParams } from "react-router";
import type {
    SpecialZoneContractCreateParams,
    SpecialZoneContractPagingRow,
    SpecialZoneContractUpdateParams
} from "@zhihaoscm/entity";
import { ZONE_CONTRACT_GOODS, ZONE_CONTRACT_SPECIAL_ZONE } from "@zhihaoscm/meta";
import { DatePicker as ZhDatePicker, DateRangePicker as ZhDateRangePicker } from "zhihao-ui-react";
import { useUserStore } from "@/views/stores";
import type { ZoneContractFormValues, ZoneContractGoodsFormRow } from "./zoneContractReactTypes";
import { zoneContractApi } from "./zoneContractReactApi";
import { ZONE_CONTRACT_QTY_MAX, ZONE_CONTRACT_UNIT_PRICE_MAX } from "./materialFormTypes";
import { formatZoneContractCustomerLabel } from "./zoneContractCustomerLabel";
import "./ZoneContractFormPage.scss";

export interface ZoneContractFormPageProps {
    contractId?: string;
    loadDetail?: (id: number) => Promise<SpecialZoneContractPagingRow>;
    createContract?: (params: SpecialZoneContractCreateParams) => Promise<unknown>;
    updateContract?: (id: number, params: SpecialZoneContractUpdateParams) => Promise<unknown>;
    checkContractCode?: (contractCode: string, excludeId?: number) => Promise<boolean>;
    permissions?: string[];
}

const defaultGoods = () =>
    ({
        id: `goods-${Date.now()}-${Math.random()}`,
        goodsName: "",
        unitPrice: undefined,
        totalTon: undefined,
        settlementRate: undefined
    }) as ZoneContractGoodsFormRow;
const loadDetailDefault = (id: number) => zoneContractApi.detail(id);

/** 保留 Vue 合同签订日期规则：今天之后的日期不可选。 */
function disableSignDateAfterToday(date: Date): boolean {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selectedDate = new Date(date);
    selectedDate.setHours(0, 0, 0, 0);
    return selectedDate.getTime() > today.getTime();
}

/** 保留 Vue 调价规则：单价生效时间不可早于当前时刻。 */
function disablePriceEffectiveTimeBeforeNow(date: Date): boolean {
    return date.getTime() < Date.now();
}

/** 将后端可能返回的空格分隔时间统一为 LocalDateTime 可解析的 ISO 格式。 */
function toIsoLocalDateTime(value: string | null | undefined): string | undefined {
    if (!value) return undefined;
    return value.includes("T") ? value : value.replace(" ", "T");
}

/** 兼容旧 Vue 表单测试及原生输入事件，确保日期文本变化同步回 Ant Form。 */
function syncDateFieldFromInput(event: ChangeEvent<HTMLDivElement>, form: FormInstance<ZoneContractFormValues>): void {
    const input = event.target as HTMLInputElement;
    const label = input.getAttribute("aria-label") || input.getAttribute("placeholder");
    const fieldMap: Record<string, keyof ZoneContractFormValues> = {
        签订日期: "signDate",
        合同开始日期: "pickStartDate",
        合同结束日期: "pickEndDate"
    };
    const field = label ? fieldMap[label] : undefined;
    if (field) form.setFieldsValue({ [field]: input.value } as Partial<ZoneContractFormValues>);
}

function toGoodsRow(goods: SpecialZoneContractPagingRow["specialZoneContractGoods"][number]): ZoneContractGoodsFormRow {
    const raw = goods as typeof goods & {
        booking_plan_qty_sum?: number;
        bookingPlanTonSum?: number;
        booking_plan_ton_sum?: number;
    };
    const bookingPlanQtySum =
        raw.bookingPlanQtySum ?? raw.booking_plan_qty_sum ?? raw.bookingPlanTonSum ?? raw.booking_plan_ton_sum;
    return {
        id: `goods-${goods.id}`,
        goodsCode: goods.goodsCode,
        goodsName: goods.goodsName || "",
        unitPrice: goods.latestUnitPrice ?? goods.unitPrice,
        originalUnitPrice: goods.latestUnitPrice ?? goods.unitPrice,
        totalTon: goods.totalTon,
        settlementRate: goods.settlementRate,
        priceEffectiveTime: toIsoLocalDateTime(goods.priceEffectiveTime),
        bookingPlanQtySum
    };
}

/** 新增/编辑合同：表单字段和提交结构严格映射 Vue add.vue。 */
export function ZoneContractFormPage({
    contractId: contractIdProp,
    loadDetail = loadDetailDefault,
    createContract = zoneContractApi.create,
    updateContract = zoneContractApi.update,
    checkContractCode,
    permissions
}: ZoneContractFormPageProps) {
    const params = useParams();
    const navigate = useNavigate();
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    const currentPermissions = permissions ?? storePermissions;
    const id = Number(contractIdProp ?? params.id);
    const editMode = Number.isFinite(id) && id > 0;
    // 旧 Vue 端要求同时具备合同处理和单价调整权限，避免仅有调价菜单权限即可修改合同。
    const canAdjustPrice =
        currentPermissions.includes("ROLE_ZONE_CONTRACT_DEAL") &&
        currentPermissions.includes("ROLE_ZONE_CONTRACT_PRICE_ADJUST");
    const [form] = Form.useForm<ZoneContractFormValues>();
    const [goods, setGoods] = useState<ZoneContractGoodsFormRow[]>([defaultGoods()]);
    const [pickRange, setPickRange] = useState<[string | null, string | null]>([null, null]);
    const [loading, setLoading] = useState(editMode);
    const [error, setError] = useState<string>();
    const [customerLabel, setCustomerLabel] = useState("");

    useEffect(() => {
        if (!editMode) return;
        let disposed = false;
        void loadDetail(id)
            .then(vo => {
                if (disposed) return;
                const core = vo.specialZoneContract;
                const customerId = vo.businessCustomer?.id ?? core.customerId;
                form.setFieldsValue({
                    customerId,
                    contractCode: core.contractCode,
                    specialZoneId: core.specialZoneId,
                    signDate: core.signDate,
                    pickStartDate: core.pickStartDate,
                    pickEndDate: core.pickEndDate
                });
                setCustomerLabel(
                    vo.businessCustomer
                        ? formatZoneContractCustomerLabel({
                              id: vo.businessCustomer.id,
                              code: vo.businessCustomer.code,
                              applyState: vo.businessCustomer.applyState,
                              institutionName: vo.businessCustomer.institutionName,
                              realName: vo.businessCustomer.realName,
                              nickName: vo.businessCustomer.nickName,
                              mobile: vo.businessCustomer.mobile
                          })
                        : String(customerId ?? "")
                );
                setPickRange([core.pickStartDate || null, core.pickEndDate || null]);
                const loadedGoods = (vo.specialZoneContractGoods || [])
                    .filter(item => item.goodsCode !== 0 && item.goodsName !== "汇总")
                    .map(toGoodsRow);
                setGoods(loadedGoods.length ? loadedGoods : [defaultGoods()]);
                setLoading(false);
            })
            .catch(() => {
                if (!disposed) {
                    setError("加载合同失败");
                    setLoading(false);
                }
            });
        return () => {
            disposed = true;
        };
    }, [editMode, form, id, loadDetail]);

    async function submit(values: ZoneContractFormValues) {
        if (values.pickEndDate && values.pickStartDate && values.pickEndDate < values.pickStartDate) {
            setError("合同结束日期不得早于开始日期");
            return;
        }
        if (
            goods.some(
                row =>
                    row.bookingPlanQtySum != null &&
                    row.totalTon != null &&
                    Number(row.totalTon) < Number(row.bookingPlanQtySum)
            )
        ) {
            setError("合同量不可小于预约单计划量总和");
            return;
        }
        if (
            editMode &&
            canAdjustPrice &&
            goods.some(row => row.priceEffectiveTime && new Date(row.priceEffectiveTime).getTime() < Date.now())
        ) {
            setError("单价生效时间不能早于当前时间");
            return;
        }
        // 生产默认提交路径保留旧 Vue 的合同号查重；测试注入提交函数时不发额外网络请求。
        const duplicateChecker =
            checkContractCode ||
            ((!editMode && createContract === zoneContractApi.create) ||
            (editMode && updateContract === zoneContractApi.update)
                ? zoneContractApi.hasDuplicateCode.bind(zoneContractApi)
                : undefined);
        if (duplicateChecker) {
            try {
                if (await duplicateChecker(values.contractCode.trim(), editMode ? id : undefined)) {
                    setError("合同号重复，请修改！");
                    return;
                }
            } catch {
                setError("校验合同编号失败，请稍后重试");
                return;
            }
        }
        const mappedGoods = goods.map(row => ({
            goodsCode:
                row.goodsCode ??
                (ZONE_CONTRACT_GOODS.list.find(item => item.intro === row.goodsName)?.code as number) ??
                0,
            unitPrice: Number(row.unitPrice),
            totalTon: Number(row.totalTon),
            settlementRate: Number(row.settlementRate),
            ...(editMode && canAdjustPrice && row.priceEffectiveTime
                ? { priceEffectiveTime: toIsoLocalDateTime(row.priceEffectiveTime) }
                : {})
        }));
        if (
            mappedGoods.some(
                item =>
                    !item.goodsCode ||
                    item.unitPrice <= 0 ||
                    item.unitPrice > ZONE_CONTRACT_UNIT_PRICE_MAX ||
                    item.totalTon <= 0 ||
                    item.totalTon > ZONE_CONTRACT_QTY_MAX ||
                    item.settlementRate <= 0 ||
                    item.settlementRate > 1
            )
        ) {
            setError("请完善物料信息");
            return;
        }
        const payload = {
            customerId: Number(values.customerId),
            contractCode: values.contractCode.trim(),
            specialZoneId: Number(values.specialZoneId),
            pickStartDate: values.pickStartDate as unknown as Record<string, unknown>,
            pickEndDate: values.pickEndDate as unknown as Record<string, unknown>,
            signDate: values.signDate as unknown as Record<string, unknown>,
            goods: mappedGoods
        } as SpecialZoneContractCreateParams;
        try {
            if (editMode) await updateContract(id, payload);
            else await createContract(payload);
            message.success(editMode ? "合同编辑成功" : "合同新增成功");
            navigate(-1);
        } catch {
            setError("合同保存失败");
        }
    }

    if (loading) return <Card loading />;
    return (
        <section aria-label="zone-contract-form-page" className="zone-contract-add-page detail-page-transparent">
            <Form
                id="zone-contract-form"
                form={form}
                className="zone-contract-add-page__form"
                layout="horizontal"
                onFinish={values => void submit(values)}
                initialValues={{}}
            >
                <div className="section-card zone-contract-add-page__page-header">
                    <div className="zone-contract-add-page__page-title">{editMode ? "编辑合同" : "新增合同"}</div>
                </div>
                <div className="section-card zone-contract-add-page__section-card zone-contract-add-page__contract-card">
                    <section className="zone-contract-add-page__section">
                        <div className="zone-contract-add-page__section-title">合同信息</div>
                        {error ? (
                            <Alert className="zone-contract-add-page__error" message={error} type="error" />
                        ) : null}
                        <div className="zone-contract-add-page__contract-grid">
                            <div>
                                <Form.Item
                                    label="采购用户"
                                    name="customerId"
                                    rules={[{ required: true, message: "请选择采购用户" }]}
                                >
                                    <InputNumber
                                        aria-label="采购用户"
                                        className="zone-contract-add-page__customer-input"
                                        min={1}
                                        disabled={editMode}
                                        placeholder="请输入机构名称/用户ID/个人实名/手机号选择"
                                        formatter={value =>
                                            editMode && customerLabel ? customerLabel : String(value ?? "")
                                        }
                                    />
                                </Form.Item>
                            </div>
                            <div>
                                <Form.Item
                                    label="合同编号"
                                    name="contractCode"
                                    rules={[
                                        { required: true, message: "请输入合同编号" },
                                        { max: 32, message: "合同编号最多 32 个字符" }
                                    ]}
                                >
                                    <Input aria-label="合同编号" maxLength={32} placeholder="请输入" />
                                </Form.Item>
                            </div>
                            <div>
                                <Form.Item
                                    label="专区名称"
                                    name="specialZoneId"
                                    rules={[{ required: true, message: "请选择专区" }]}
                                >
                                    <Select
                                        aria-label="专区名称"
                                        options={ZONE_CONTRACT_SPECIAL_ZONE.options.map(item => ({
                                            value: item.value,
                                            label: item.label
                                        }))}
                                        disabled={editMode}
                                        placeholder="请选择专区"
                                        showSearch
                                        onSearch={value => {
                                            const numericValue = Number(value);
                                            if (
                                                ZONE_CONTRACT_SPECIAL_ZONE.options.some(
                                                    item => item.value === numericValue
                                                )
                                            ) {
                                                form.setFieldsValue({ specialZoneId: numericValue });
                                            }
                                        }}
                                    />
                                </Form.Item>
                            </div>
                            <div>
                                <Form.Item
                                    label="签订日期"
                                    name="signDate"
                                    rules={[{ required: true, message: "请选择签订日期" }]}
                                >
                                    <div onChange={event => syncDateFieldFromInput(event, form)}>
                                        <ZhDatePicker
                                            aria-label="签订日期"
                                            format="YYYY-MM-DD"
                                            placeholder="请选择合同签订日期"
                                            type="date"
                                            valueFormat="YYYY-MM-DD"
                                            disabledDate={disableSignDateAfterToday}
                                        />
                                    </div>
                                </Form.Item>
                            </div>
                            <div>
                                <Form.Item label="合同日期" required>
                                    <div onChange={event => syncDateFieldFromInput(event, form)}>
                                        <ZhDateRangePicker
                                            aria-label="合同日期"
                                            endPlaceholder="合同结束日期"
                                            format="YYYY-MM-DD"
                                            rangeSeparator="-"
                                            startPlaceholder="合同开始日期"
                                            type="daterange"
                                            value={pickRange}
                                            valueFormat="YYYY-MM-DD"
                                            onChange={(_value, formattedValue) => {
                                                const nextRange: [string | null, string | null] = formattedValue
                                                    ? [formattedValue[0] || null, formattedValue[1] || null]
                                                    : [null, null];
                                                setPickRange(nextRange);
                                                form.setFieldsValue({
                                                    pickStartDate: nextRange[0] || undefined,
                                                    pickEndDate: nextRange[1] || undefined
                                                });
                                            }}
                                        />
                                    </div>
                                </Form.Item>
                            </div>
                        </div>
                        <Form.Item
                            hidden
                            name="pickStartDate"
                            rules={[{ required: true, message: "请选择合同开始日期" }]}
                        >
                            <Input />
                        </Form.Item>
                        <Form.Item
                            hidden
                            name="pickEndDate"
                            rules={[{ required: true, message: "请选择合同结束日期" }]}
                        >
                            <Input />
                        </Form.Item>
                    </section>
                </div>
                <div className="section-card zone-contract-add-page__section-card zone-contract-add-page__material-card">
                    <section className="zone-contract-add-page__section zone-contract-add-page__section--materials">
                        <div className="zone-contract-material-form__head">
                            <div className="zone-contract-material-form__title">物料信息</div>
                            {!editMode ? (
                                <Button type="link" onClick={() => setGoods(current => [...current, defaultGoods()])}>
                                    增加物料
                                </Button>
                            ) : null}
                        </div>
                        <div className="zone-contract-material-form__rows">
                            {goods.map((row, index) => (
                                <div key={row.id} className="zone-contract-material-form__row">
                                    <div className="zone-contract-material-form__grid">
                                        <div className="zone-contract-material-form__cell">
                                            <Form.Item label="物料名称" required>
                                                <Select
                                                    aria-label={`物料名称${index + 1}`}
                                                    value={row.goodsName}
                                                    disabled={editMode}
                                                    options={ZONE_CONTRACT_GOODS.list.map(item => ({
                                                        value: item.intro,
                                                        label: item.intro
                                                    }))}
                                                    placeholder="请选择物料"
                                                    showSearch
                                                    onSearch={value => {
                                                        if (
                                                            ZONE_CONTRACT_GOODS.list.some(item => item.intro === value)
                                                        ) {
                                                            setGoods(current =>
                                                                current.map((item, i) =>
                                                                    i === index
                                                                        ? {
                                                                              ...item,
                                                                              goodsName: value,
                                                                              goodsCode: ZONE_CONTRACT_GOODS.list.find(
                                                                                  meta => meta.intro === value
                                                                              )?.code as number
                                                                          }
                                                                        : item
                                                                )
                                                            );
                                                        }
                                                    }}
                                                    onChange={value =>
                                                        setGoods(current =>
                                                            current.map((item, i) =>
                                                                i === index
                                                                    ? {
                                                                          ...item,
                                                                          goodsName: String(value || ""),
                                                                          goodsCode: ZONE_CONTRACT_GOODS.list.find(
                                                                              meta => meta.intro === value
                                                                          )?.code as number
                                                                      }
                                                                    : item
                                                            )
                                                        )
                                                    }
                                                />
                                            </Form.Item>
                                        </div>
                                        <div className="zone-contract-material-form__cell">
                                            <Form.Item label="单价" required>
                                                <InputNumber
                                                    aria-label={`单价${index + 1}`}
                                                    min={0}
                                                    max={ZONE_CONTRACT_UNIT_PRICE_MAX}
                                                    disabled={editMode && !canAdjustPrice}
                                                    placeholder="请输入"
                                                    suffix="元"
                                                    value={row.unitPrice}
                                                    onChange={value =>
                                                        setGoods(current =>
                                                            current.map((item, i) =>
                                                                i === index
                                                                    ? { ...item, unitPrice: value ?? undefined }
                                                                    : item
                                                            )
                                                        )
                                                    }
                                                />
                                            </Form.Item>
                                        </div>
                                        <div className="zone-contract-material-form__cell">
                                            <Form.Item label="结算率" required>
                                                <InputNumber
                                                    aria-label={`结算率${index + 1}`}
                                                    min={0}
                                                    max={1}
                                                    placeholder="请输入"
                                                    value={row.settlementRate}
                                                    onChange={value =>
                                                        setGoods(current =>
                                                            current.map((item, i) =>
                                                                i === index
                                                                    ? { ...item, settlementRate: value ?? undefined }
                                                                    : item
                                                            )
                                                        )
                                                    }
                                                />
                                            </Form.Item>
                                        </div>
                                        <div className="zone-contract-material-form__cell">
                                            <Form.Item label="合同量" required>
                                                <InputNumber
                                                    aria-label={`合同量${index + 1}`}
                                                    min={0}
                                                    max={ZONE_CONTRACT_QTY_MAX}
                                                    placeholder="请输入"
                                                    suffix="吨"
                                                    value={row.totalTon}
                                                    onChange={value =>
                                                        setGoods(current =>
                                                            current.map((item, i) =>
                                                                i === index
                                                                    ? { ...item, totalTon: value ?? undefined }
                                                                    : item
                                                            )
                                                        )
                                                    }
                                                />
                                            </Form.Item>
                                        </div>
                                        {editMode && canAdjustPrice ? (
                                            <div className="zone-contract-material-form__cell">
                                                <Form.Item label="单价生效时间" required>
                                                    <ZhDatePicker
                                                        aria-label={`单价生效时间${index + 1}`}
                                                        format="YYYY-MM-DD HH:mm:ss"
                                                        placeholder="请选择单价生效时间"
                                                        type="datetime"
                                                        value={row.priceEffectiveTime}
                                                        valueFormat="YYYY-MM-DDTHH:mm:ss"
                                                        disabledDate={disablePriceEffectiveTimeBeforeNow}
                                                        onChange={(_value, formattedValue) => {
                                                            // 日期组件允许返回数组，表单行只保留单值并将清空状态转换为 undefined。
                                                            const nextPriceEffectiveTime = Array.isArray(formattedValue)
                                                                ? formattedValue[0]
                                                                : formattedValue;
                                                            setGoods(current =>
                                                                current.map((item, i) =>
                                                                    i === index
                                                                        ? {
                                                                              ...item,
                                                                              // 保留 ISO 值，确保提交到 LocalDateTime 字段时可被后端解析。
                                                                              priceEffectiveTime:
                                                                                  nextPriceEffectiveTime ?? undefined
                                                                          }
                                                                        : item
                                                                )
                                                            );
                                                        }}
                                                    />
                                                </Form.Item>
                                            </div>
                                        ) : null}
                                    </div>
                                    {!editMode && goods.length > 1 ? (
                                        <Button
                                            aria-label={`删除物料${index + 1}`}
                                            type="link"
                                            onClick={() => setGoods(current => current.filter((_, i) => i !== index))}
                                        >
                                            删除物料
                                        </Button>
                                    ) : null}
                                </div>
                            ))}
                        </div>
                    </section>
                </div>
                <div className="operation-fixed-btn zone-contract-add-page__actions">
                    <Button aria-label="提交专区合同" htmlType="submit">
                        提交
                    </Button>
                    <Button onClick={() => navigate(-1)}>返回</Button>
                </div>
            </Form>
        </section>
    );
}

export default ZoneContractFormPage;
